import { __extends, __assign } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { Swipeable } from 'react-swipeable';
import { UitkPagingPrevButton, UitkPagingNextButton } from 'uitk-react-button';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { classNames, analyticsTracker, liveAnnounce, Level } from 'uitk-react-utils';
import styles from './uitk-media-gallery.css';
import { UitkMediaGalleryImage } from './uitk-media-gallery-image.esm.js';
import { UitkMediaGalleryImageFooter } from './uitk-media-gallery-image-footer.esm.js';

var getClassNameList = classNames.bind(styles);
var UitkMediaGalleryLocalAnalyticsIdType;
(function (UitkMediaGalleryLocalAnalyticsIdType) {
    UitkMediaGalleryLocalAnalyticsIdType["clickLeft"] = "clickLeft";
    UitkMediaGalleryLocalAnalyticsIdType["clickRight"] = "clickRight";
})(UitkMediaGalleryLocalAnalyticsIdType || (UitkMediaGalleryLocalAnalyticsIdType = {}));
var MediaGalleryImageView = /** @class */ (function (_super) {
    __extends(MediaGalleryImageView, _super);
    function MediaGalleryImageView(props) {
        var _this = _super.call(this, props) || this;
        _this.uid = Math.random().toString(36).substring(7);
        _this.mediaGalleryLiveAnnounceId = "uitk-media-gallery-live-announce-".concat(_this.uid);
        _this.state = {
            currentIndex: props.currentIndex || 0,
            isInteractive: false
        };
        _this.onNextCallback = _this.onNextCallback.bind(_this);
        _this.onNextTrackingCallback = _this.onNextTrackingCallback.bind(_this);
        _this.onPrevCallback = _this.onPrevCallback.bind(_this);
        _this.onPrevTrackingCallback = _this.onPrevTrackingCallback.bind(_this);
        _this.onUpdateCallback = _this.onUpdateCallback.bind(_this);
        _this.navWithArrowKeys = _this.navWithArrowKeys.bind(_this);
        _this.bindEvents = _this.bindEvents.bind(_this);
        _this.unbindEvents = _this.unbindEvents.bind(_this);
        _this.onSwiping = _this.onSwiping.bind(_this);
        _this.contentRef = React.createRef();
        return _this;
    }
    MediaGalleryImageView.prototype.componentDidMount = function () {
        this.setState({
            isAnimating: false,
            isInteractive: true
        });
        this.bindEvents();
    };
    MediaGalleryImageView.prototype.componentWillUnmount = function () {
        clearInterval(this._animationTimer);
        this.unbindEvents();
    };
    MediaGalleryImageView.prototype.onPrevTrackingCallback = function () {
        var _a = this.props, analytics = _a.analytics, galleryName = _a.galleryName;
        var clickLeftAnalytics = analytics && __assign({ localAnalyticsId: "".concat(galleryName, ".MediaGallery.").concat(UitkMediaGalleryLocalAnalyticsIdType.clickLeft) }, analytics);
        analyticsTracker(clickLeftAnalytics, '', '');
    };
    MediaGalleryImageView.prototype.onNextTrackingCallback = function () {
        var _a = this.props, analytics = _a.analytics, galleryName = _a.galleryName;
        var clickRightAnalytics = analytics && __assign({ localAnalyticsId: "".concat(galleryName, ".MediaGallery.").concat(UitkMediaGalleryLocalAnalyticsIdType.clickRight) }, analytics);
        analyticsTracker(clickRightAnalytics, '', '');
    };
    MediaGalleryImageView.prototype.onPrevCallback = function () {
        var _this = this;
        var isAnimating = this.state.isAnimating;
        if (isAnimating) {
            return;
        }
        this.setState({
            isAnimating: true,
            isAnimatingNext: false,
            isAnimatingPrev: true
        });
        if (this._animationTimer) {
            clearInterval(this._animationTimer);
        }
        var prevIndex = this.getBackwardIndex(1);
        this._animationTimer = setTimeout(function () {
            _this.announce(prevIndex);
            _this.onUpdateCallback(prevIndex);
            _this.onPrevTrackingCallback();
        }, 300);
    };
    MediaGalleryImageView.prototype.onNextCallback = function () {
        var _this = this;
        var isAnimating = this.state.isAnimating;
        if (isAnimating) {
            return;
        }
        this.setState({
            isAnimating: true,
            isAnimatingNext: true,
            isAnimatingPrev: false
        });
        if (this._animationTimer) {
            clearInterval(this._animationTimer);
        }
        var nextIndex = this.getForwardIndex(1);
        this._animationTimer = setTimeout(function () {
            _this.announce(nextIndex);
            _this.onUpdateCallback(nextIndex);
            _this.onNextTrackingCallback();
        }, 300);
    };
    MediaGalleryImageView.prototype.onUpdateCallback = function (currentIndex) {
        var onIndexChange = this.props.onIndexChange;
        if (typeof onIndexChange === 'function') {
            onIndexChange(currentIndex);
        }
    };
    MediaGalleryImageView.prototype.onSwiping = function (event) {
        var images = this.props.images;
        var isInteractive = this.state.isInteractive;
        var canSwipe = isInteractive && images && images.length > 1;
        if (canSwipe && event.dir === 'Left') {
            this.onNextCallback();
        }
        if (canSwipe && event.dir === 'Right') {
            this.onPrevCallback();
        }
    };
    MediaGalleryImageView.prototype.getForwardIndex = function (count) {
        var images = this.props.images;
        if (images && images.length > 0 && count > 0) {
            var softIndex = this.currentIndex + count;
            return Math.abs(softIndex % images.length);
        }
        return this.currentIndex;
    };
    MediaGalleryImageView.prototype.getBackwardIndex = function (count) {
        var images = this.props.images;
        if (images && images.length > 0 && count > 0) {
            if (images.length === 1) {
                return 0;
            }
            var softIndex = this.currentIndex - count;
            if (softIndex >= 0) {
                return softIndex;
            }
            var lastValue = Math.abs(images.length - Math.abs(softIndex % images.length));
            return lastValue;
        }
        return 0;
    };
    Object.defineProperty(MediaGalleryImageView.prototype, "currentIndex", {
        // Getter / setter for this.currentIndex
        get: function () {
            var currentIndex = this.state.currentIndex;
            return currentIndex;
        },
        set: function (value) {
            this.setState({ currentIndex: value });
        },
        enumerable: false,
        configurable: true
    });
    MediaGalleryImageView.prototype.announce = function (index) {
        var _a = this.props, images = _a.images, l10n = _a.l10n;
        var currentIndex = this.state.currentIndex;
        this.setState({
            currentIndex: index,
            isAnimating: false,
            isAnimatingNext: false,
            isAnimatingPrev: false
        });
        liveAnnounce(l10n.formatText('mediagallery.imageChange', currentIndex + 1, images.length, images[index].caption), Level.POLITE, undefined, this.contentRef, this.mediaGalleryLiveAnnounceId);
    };
    MediaGalleryImageView.prototype.navWithArrowKeys = function (e) {
        var keyNames = {
            LEFT: 37,
            RIGHT: 39
        };
        if (e.keyCode === keyNames.LEFT) {
            this.onPrevCallback();
        }
        else if (e.keyCode === keyNames.RIGHT) {
            this.onNextCallback();
        }
    };
    MediaGalleryImageView.prototype.bindEvents = function () {
        var body = document.body;
        body.addEventListener('keydown', this.navWithArrowKeys);
    };
    MediaGalleryImageView.prototype.unbindEvents = function () {
        var body = document.body;
        body.removeEventListener('keydown', this.navWithArrowKeys);
    };
    MediaGalleryImageView.prototype.render = function () {
        var _a = this.state, currentIndex = _a.currentIndex, isAnimating = _a.isAnimating, isAnimatingNext = _a.isAnimatingNext, isAnimatingPrev = _a.isAnimatingPrev;
        var _b = this.props, images = _b.images, l10n = _b.l10n, videoAnalytics = _b.videoAnalytics, stretchToFill = _b.stretchToFill, videoButtonText = _b.videoButtonText;
        var imageAnimationClass = '';
        if (isAnimating) {
            if (isAnimatingPrev) {
                imageAnimationClass = 'uitk-media-gallery-image-animating-prev';
            }
            else if (isAnimatingNext) {
                imageAnimationClass = 'uitk-media-gallery-image-animating-next';
            }
        }
        var imgCaption = images[currentIndex].caption || '';
        var uitkFlexBaseClass = 'uitk-flex';
        var uitkMediaGalleryImageBaseClass = 'uitk-media-gallery-image';
        var uitkMediaGalleryImagePrevClassNames = getClassNameList("".concat(uitkMediaGalleryImageBaseClass, "-prev"), uitkFlexBaseClass, "".concat(uitkFlexBaseClass, "-align-items-center"));
        var uitkMediaGalleryImageNextClassNames = getClassNameList("".concat(uitkMediaGalleryImageBaseClass, "-next"), uitkFlexBaseClass, "".concat(uitkFlexBaseClass, "-align-items-center"));
        var uitkMediaGalleryControlsBaseClass = 'uitk-media-gallery-controls';
        var uitkMediaGalleryControlsPrevClassNames = getClassNameList(uitkMediaGalleryControlsBaseClass, "".concat(uitkMediaGalleryControlsBaseClass, "-prev"));
        var uitkMediaGalleryControlsNextClassNames = getClassNameList(uitkMediaGalleryControlsBaseClass, "".concat(uitkMediaGalleryControlsBaseClass, "-next"));
        return (images &&
            images.length > 0 && (React.createElement("div", { className: getClassNameList("".concat(uitkMediaGalleryImageBaseClass, "-view"), imageAnimationClass), ref: this.contentRef },
            images.length > 1 && (React.createElement(React.Fragment, null,
                React.createElement(UitkMediaGalleryImage, __assign({}, images[this.getBackwardIndex(1)], { className: uitkMediaGalleryImagePrevClassNames, ariaHidden: true, stretchToFill: stretchToFill })),
                React.createElement(Swipeable, { onSwiping: this.onSwiping, delta: 100 },
                    React.createElement(UitkMediaGalleryImage, __assign({}, images[this.currentIndex], { className: getClassNameList("".concat(uitkMediaGalleryImageBaseClass, "-active")), videoAnalytics: videoAnalytics, videoButtonText: videoButtonText, stretchToFill: stretchToFill }))),
                React.createElement(UitkMediaGalleryImage, __assign({}, images[this.getForwardIndex(1)], { className: uitkMediaGalleryImageNextClassNames, ariaHidden: true, stretchToFill: stretchToFill })),
                React.createElement(UitkMediaGalleryImage, __assign({}, images[this.getForwardIndex(2)], { className: getClassNameList("".concat(uitkMediaGalleryImageBaseClass, "-queued")), ariaHidden: true, stretchToFill: stretchToFill })),
                React.createElement(UitkLayoutFlex, { className: getClassNameList("".concat(uitkMediaGalleryControlsBaseClass, "-wrapper")), justifyContent: "space-between" },
                    React.createElement(UitkPagingPrevButton, { className: uitkMediaGalleryControlsPrevClassNames, onClick: this.onPrevCallback },
                        React.createElement("span", { className: "is-visually-hidden", "data-a11ytext": "true" }, l10n.formatText('mediagallery.previousImage'))),
                    React.createElement(UitkPagingNextButton, { className: uitkMediaGalleryControlsNextClassNames, onClick: this.onNextCallback },
                        React.createElement("span", { className: "is-visually-hidden", "data-a11ytext": "true" }, l10n.formatText('mediagallery.nextImage')))))),
            images.length === 1 && (React.createElement(UitkLayoutFlexItem, { minWidth: "full_width", shrink: 0 },
                React.createElement(UitkMediaGalleryImage, __assign({}, images[this.currentIndex], { className: getClassNameList('uitk-media-gallery-image-active'), videoAnalytics: videoAnalytics, videoButtonText: videoButtonText, stretchToFill: stretchToFill })))),
            React.createElement(UitkMediaGalleryImageFooter, { imgCaption: imgCaption, currentIndex: currentIndex, imagesLength: images.length }))));
    };
    MediaGalleryImageView.defaultProps = {
        currentIndex: 0,
        stretchToFill: true
    };
    return MediaGalleryImageView;
}(React.PureComponent));
var UitkMediaGalleryImageView = function (props) {
    var l10n = useLocalization();
    return React.createElement(MediaGalleryImageView, __assign({ l10n: l10n }, props));
};

export { UitkMediaGalleryImageView, UitkMediaGalleryLocalAnalyticsIdType };
